import { useMemo, useState } from "react";
import { ProgressBar, ScreenLoading, StatusChip } from "../components";
import type { AreaRow, Me } from "../lib/api";
import { placeLine, STATUS } from "../lib/status";

/**
 * The address list, which is the app's front door.
 *
 * The requirement was "a dashboard tab listing locations by address for easy
 * navigation", and the reason it is the first thing you see rather than a tab is
 * that nobody navigates a map by looking at polygons. A surveyor in Kibinong
 * knows the name of the jalan and the name of the owner, not the coordinates of
 * a rectangle, so the list is what they search and the map is what they land on.
 *
 * Blocks before Ditolak so the things that need attention are visible without
 * scrolling or filtering.
 */
export function Areas({
  areas,
  selected,
  onSelect,
  onRefresh,
  canCreate,
  onDrawn,
  me,
}: {
  areas: AreaRow[] | null;
  selected: number | null;
  onSelect: (id: number | null) => void;
  onRefresh: () => Promise<void> | void;
  canCreate: boolean;
  onDrawn: (id: number) => void;
  me: Me;
}) {
  const [q, setQ] = useState("");
  const [only, setOnly] = useState<"all" | "permitted" | "in_progress" | "blocked">("all");

  const list = areas ?? [];
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return list
      .filter((a) => (only === "all" ? true : a.status === only))
      .filter((a) =>
        !needle
          ? true
          : [a.address_detail, a.kelurahan, a.kecamatan, a.kabupaten_kota, a.rt, a.rw, a.postcode]
              .filter(Boolean)
              .join(" ")
              .toLowerCase()
              .includes(needle),
      );
  }, [list, q, only]);

  const counts = useMemo(
    () => ({
      all: list.length,
      permitted: list.filter((a) => a.status === "permitted").length,
      in_progress: list.filter((a) => a.status === "in_progress").length,
      blocked: list.filter((a) => a.status === "blocked").length,
    }),
    [list],
  );

  // The first fetch has not landed: a centered loader, never the empty
  // state. Printing "no areas" here is the phantom flash. After all hooks.
  if (areas === null) return <ScreenLoading label="Memuat area…" />;

  return (
    <aside className="sidebar">
      <div className="list-head">
        <input
          className="input search"
          placeholder="Cari alamat, kelurahan, kode pos…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Cari area"
        />
      </div>
      <div className="filter-row" style={{ padding: "8px 14px", borderBottom: "1px solid var(--border)" }}>
        {(
          [
            ["all", "Semua"],
            // From STATUS, not from here. These four words were hard-coded in this
            // file as well as in status.ts, the two disagreed, and the disagreement
            // is how a blocked area ended up labelled "Ditolak" while a refused
            // decision was also "Ditolak". One source of truth.
            ["blocked", STATUS.blocked.short],
            ["in_progress", STATUS.in_progress.short],
            ["permitted", STATUS.permitted.short],
          ] as const
        ).map(([k, label]) => (
          // Tabular filter: the label and the count live in aligned slots so
          // every button's number sits on the same column whatever the word
          // length. The count always renders (0 included) so the active
          // button never shifts width when its count changes.
          <button
            key={k}
            className={`btn btn-sm filter-btn ${only === k ? "btn-primary" : ""}`}
            onClick={() => setOnly(k)}
          >
            <span className="filter-label">{label}</span>{" "}
            <span className="mono filter-count">{counts[k]}</span>
          </button>
        ))}
      </div>

      <div className="list">
        {shown.length === 0 && (
          <p className="mute small" style={{ padding: 16 }}>
            {areas.length === 0
              ? "Belum ada area. Gunakan tombol “Gambar Area” di peta untuk membuat yang pertama."
              : "Tidak ada area yang cocok."}
          </p>
        )}
        {shown.map((a) => (
          <button
            key={a.area_id}
            className="row"
            aria-current={a.area_id === selected}
            onClick={() => onSelect(a.area_id === selected ? null : a.area_id)}
          >
            <div className="row-top">
              <span className="row-addr">{a.address_detail || `Kel. ${a.kelurahan}`}</span>
              <StatusChip status={a.status} />
            </div>
            <div className="row-meta">{placeLine(a)}</div>
            {a.postcode && <div className="row-meta">{a.postcode}</div>}
            <ProgressBar a={a} />
            <div className="row-note mono">
              {a.granted_count}/{a.required_count} persetujuan
              {a.refused_count > 0 ? ` · ${a.refused_count} ditolak` : ""}
            </div>
          </button>
        ))}
      </div>

      <div className="rowflex" style={{ padding: 10, borderTop: "1px solid var(--border)" }}>
        <span className="small mute">
          {areas.length} area · {me.roles.join(", ")}
        </span>
        <div className="spacer" />
        <button
          className="btn btn-sm btn-ghost"
          title="Muat ulang"
          onClick={() => void onRefresh()}
          disabled={!canCreate}
        >
          ⟳
        </button>
      </div>
    </aside>
  );
}
